import instance from '.'
import type { AxiosResponse } from 'axios'
import { err, getResult } from '../utils/functions'
import { APIResult } from '../utils/types'
import {
  Poll,
  PollItem,
  QuestionResponsePage,
  QuestionResults,
  QuestionGame,
  QuestionGuess,
} from './types/polls'
import { UserSoloStats } from './types/users'

export const QUESTION_EXPERIENCE_VERSION =
  import.meta.env.VITE_LIVING_QUESTIONS_ENABLED === 'false' ? 1 : 2

const RANDOM_GUEST_SESSION_STORAGE_KEY = 'random_guest_session_id'

export function getPollGuestKey(pollId: number): string {
  const storageKey = `poll_guest_key_${pollId}`
  let key = localStorage.getItem(storageKey)
  if (!key) {
    key = `poll_${pollId}_${globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`
    localStorage.setItem(storageKey, key)
  }
  return key
}

const pollRequestError = (error: {
  response?: AxiosResponse
  message?: string
}): AxiosResponse =>
  error.response ||
  ({
    data: {
      success: false,
      message: error.message || 'Could not load this poll. Please try again.',
    },
  } as AxiosResponse)

const getRandomGuestSessionId = (): string | undefined => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined')
    return undefined
  const existing = localStorage.getItem(RANDOM_GUEST_SESSION_STORAGE_KEY)
  if (existing && /^[a-zA-Z0-9_-]{8,128}$/.test(existing)) {
    return existing
  }
  const created = `g_${Math.random().toString(36).slice(2, 12)}_${Date.now().toString(36)}`
  localStorage.setItem(RANDOM_GUEST_SESSION_STORAGE_KEY, created)
  return created
}

export function createPoll(params: {
  poll: Poll
  items: PollItem[]
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/create', params)
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function updatePoll(params: {
  pollId: number
  tags?: string
  category?: string
  prompt?: string
  visibility?: string
  pollResultsVisibility?: 'hidden' | 'visible'
  showSubmittedAnswers?: boolean
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/update', params)
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function updatePollItems(params: {
  pollId: number
  items: { id?: number; title: string }[]
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/update-items', params)
    .catch((e) => err(e))
    .then((res) => getResult<Poll>(res))
}

export async function getPolls(params?: {
  pollFormat?: Poll['pollFormat']
  page?: number
  limit?: number
  search?: string
  tags?: string[]
  categories?: string[]
  states?: string[]
  visibility?: string[]
  sort?: string
  creatorId?: number
  participantKeys?: string[]
  onlyParticipated?: boolean
}): Promise<APIResult<{ polls: Poll[]; total: number }>> {
  return instance
    .get('/api/poll', {
      params: {
        ...params,
        includeCommunityAnswers: true,
        questionExperienceVersion: QUESTION_EXPERIENCE_VERSION,
      },
    })
    .catch((e) => err(e))
    .then((res) => getResult<{ polls: Poll[]; total: number }>(res))
}

export function getPollTags(): Promise<APIResult<string[]>> {
  return instance
    .get('/api/poll/tags')
    .catch((e) => err(e))
    .then((res) => getResult<string[]>(res))
}

export function getPollCategories(): Promise<APIResult<string[]>> {
  return instance
    .get('/api/poll/categories')
    .catch((e) => err(e))
    .then((res) => getResult<string[]>(res))
}

export function getPoll(
  id: number,
  params?: { localKey?: string },
): Promise<APIResult<Poll>> {
  return instance
    .get(`/api/poll/${id}`, {
      params: {
        ...params,
        localKey:
          params?.localKey ||
          localStorage.getItem(`poll_guest_key_${id}`) ||
          undefined,
        includeCommunityAnswers: true,
        questionExperienceVersion: QUESTION_EXPERIENCE_VERSION,
      },
    })
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function submitPollAnswer(params: {
  pollId: number
  answerItemId?: number
  answerText?: string
  displayLabel?: string
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/answer', {
      ...params,
      questionExperienceVersion: QUESTION_EXPERIENCE_VERSION,
      localKey: getPollGuestKey(params.pollId),
    })
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function closePollAnswers(pollId: number): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/close-answers', {
      pollId,
      questionExperienceVersion: QUESTION_EXPERIENCE_VERSION,
      localKey: getPollGuestKey(pollId),
    })
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function hidePollAnswer(
  pollId: number,
  answerItemId: number,
): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/hide-answer', {
      pollId,
      answerItemId,
      questionExperienceVersion: QUESTION_EXPERIENCE_VERSION,
      localKey: getPollGuestKey(pollId),
    })
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export async function getRandomPoll(params?: {
  excludeId?: number
}): Promise<APIResult<Poll>> {
  const guestSessionId = getRandomGuestSessionId()
  return instance
    .get('/api/poll/random', {
      params: {
        ...(params || {}),
        ...(guestSessionId ? { guestSessionId } : {}),
      },
    })
    .catch((e) => err(e))
    .then((res) => getResult<Poll>(res))
}

export async function getDailyPoll(params?: {
  type?: 'today' | 'previous'
  localKey?: string
}): Promise<APIResult<Poll>> {
  return instance
    .get('/api/poll/daily', {
      params: params || {},
    })
    .catch((e) => err(e))
    .then((res) => getResult<Poll>(res))
}

export function submitPoll(params: {
  pollId: number
  ranking: (number | string)[]
  localKey?: string
  ownerSubmission?: boolean
  ownerExtraIndex?: number
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/submit', params)
    .catch((e) => err(e))
    .then((res) => getResult<Poll>(res))
}

export function removePollSubmission(params: {
  pollId: number
  localKey?: string
  ownerExtraIndex?: number
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/remove', params)
    .catch((e) => err(e))
    .then((res) => getResult<Poll>(res))
}

export function ratePoll(params: {
  pollId: number
  rating: number
  localKey?: string
}): Promise<APIResult<Poll>> {
  return instance
    .post('/api/poll/rate', params)
    .catch(pollRequestError)
    .then((res) => getResult<Poll>(res))
}

export function playedPoll(pollId: number): Promise<APIResult<void>> {
  return instance
    .post('/api/poll/played', { pollId })
    .catch((e) => err(e))
    .then((res) => getResult<void>(res))
}

export function awardDailyPollPlayed(params: {
  pollId: number
  correct: number
  total: number
  guesses: number
  completionMs: number
}): Promise<
  APIResult<{
    stats: UserSoloStats
    xpAwarded: number
  }>
> {
  return instance
    .post('/api/poll/daily-played', params)
    .catch((e) => err(e))
    .then((res) =>
      getResult<{
        stats: UserSoloStats
        xpAwarded: number
      }>(res),
    )
}

export function deletePoll(pollId: number): Promise<APIResult<boolean>> {
  return instance
    .delete(`/api/poll/${pollId}`)
    .catch(pollRequestError)
    .then((res) => getResult<boolean>(res))
}

export function reportPoll(params: {
  pollId: number
  reason?: string
  details?: string
}): Promise<APIResult<void>> {
  return instance
    .post('/api/poll/report', params)
    .catch(pollRequestError)
    .then((res) => getResult<void>(res))
}

const questionPost = <T>(operation: string, pollId: number, payload: object) =>
  instance
    .post(`/api/poll/${operation}`, {
      ...payload,
      pollId,
      localKey: getPollGuestKey(pollId),
      questionExperienceVersion: 2,
    })
    .catch(pollRequestError)
    .then((res) => getResult<T>(res))
export function getQuestionResponses(
  pollId: number,
  cursor?: string,
): Promise<APIResult<QuestionResponsePage>> {
  return instance
    .get(`/api/poll/${pollId}/responses`, {
      params: {
        questionExperienceVersion: 2,
        localKey: getPollGuestKey(pollId),
        cursor,
      },
    })
    .catch(pollRequestError)
    .then((res) => getResult<QuestionResponsePage>(res))
}
export function getQuestionResults(
  pollId: number,
): Promise<APIResult<QuestionResults>> {
  return instance
    .get(`/api/poll/${pollId}/question-results`, {
      params: {
        questionExperienceVersion: 2,
        localKey: getPollGuestKey(pollId),
      },
    })
    .catch(pollRequestError)
    .then((res) => getResult<QuestionResults>(res))
}
export const refreshQuestionRanking = (pollId: number) =>
  questionPost<Poll>('refresh-question-ranking', pollId, {})
export const copyQuestion = (pollId: number) =>
  questionPost<Poll>('copy-question', pollId, {})
export const startQuestionGame = (
  pollId: number,
  mode: QuestionGame['mode'],
  idempotencyKey: string,
  lobbyCode?: string,
) =>
  questionPost<QuestionGame>('question-play', pollId, {
    mode,
    idempotencyKey,
    lobbyCode,
  })
export const submitQuestionGuess = (
  pollId: number,
  sessionId: string,
  order: string[],
  requestId: string,
) =>
  questionPost<QuestionGuess>('question-guess', pollId, {
    sessionId,
    order,
    requestId,
  })
export const finishQuestionLocal = (
  pollId: number,
  sessionId: string,
  correct: number,
  won: boolean,
) =>
  questionPost<Pick<QuestionGuess, 'result' | 'stats'>>(
    'question-finish-local',
    pollId,
    { sessionId, correct, won },
  )
